import React, { useState } from "react";
import { useDMOps } from "../../../hooks/useDMOps";
import { DMDraft, defaultDMDraft } from "../../../context/MonyawnContext.types";

type Props = {
  onClose: () => void;
};

export const ComposeDMModal: React.FC<Props> = ({ onClose }) => {
  const { handleDMSubmit } = useDMOps();
  const [draft, setDraft] = useState<DMDraft>(defaultDMDraft);
  const [error, setError] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.body.trim()) {
      setError("Write something before saving.");
      return;
    }
    handleDMSubmit(draft);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-6 sm:p-8 space-y-5 animate-in slide-in-from-bottom-4 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-accent">
              DM control
            </p>
            <h2 className="text-xl font-bold text-brand-ink mt-1">Write the message</h2>
          </div>
          <button
            onClick={onClose}
            className="text-brand-muted hover:text-brand-ink transition-colors mt-1"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                Channel
              </span>
              <select
                className="w-full px-3 py-2.5 rounded-2xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none appearance-none"
                value={draft.channel}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, channel: e.target.value as DMDraft["channel"] }))
                }
              >
                <option value="email">Email</option>
                <option value="linkedin">LinkedIn</option>
                <option value="note">Private note</option>
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                Subject
              </span>
              <input
                className="w-full px-3 py-2.5 rounded-2xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none"
                placeholder="Keep it direct"
                value={draft.subject}
                onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
              />
            </label>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
              Message
            </span>
            <textarea
              className="w-full px-3 py-2.5 rounded-2xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none resize-none"
              rows={7}
              placeholder="Draft it here. Signals like emails, dates, and names will be extracted automatically."
              value={draft.body}
              onChange={(e) => {
                setError("");
                setDraft((d) => ({ ...d, body: e.target.value }));
              }}
            />
          </label>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              className="flex-1 bg-brand-accent text-white py-3 rounded-2xl font-bold hover:bg-brand-accent-strong transition-all shadow-sm active:scale-95"
            >
              Save draft
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-brand-surface border border-black/10 text-brand-ink px-6 py-3 rounded-2xl font-medium hover:bg-black/5 transition-all active:scale-95"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
